/**
 * Imusic 弥散背景 — Apple Music 播放页风格
 * 从封面提取 3 色，OKLCH 处理后在超大 blur 下自然融合。
 */

const COLOR_CACHE = new Map();
const CACHE_MAX_SIZE = 48;
const CACHE_VERSION = 'apple-music-v10';

const NEUTRAL_IMAGE_CHROMA = 0.024;

/** 显示用 OKLCH 区间：柔化但不脱离封面本色（下限放低，让暗封面保住深色底蕴） */
const DISPLAY_L_MIN = 0.28;
const DISPLAY_L_MAX = 0.76;
const DISPLAY_CHROMA_MAX = 0.14;

/** 过暗/过灰像素不参与「情绪主色」竞选，避免阴影面积压过红卫衣这类焦点色 */
const MOOD_MIN_LIGHTNESS = 0.12;
const MOOD_SHADOW_CHROMA = 0.035;

const THEME_FALLBACK_HEX = {
  pink: ['#c4a0b0', '#d4b4c0', '#b490a4'],
  blue: ['#98b4d0', '#a8c4dc', '#88a4c4'],
  green: ['#94b8a8', '#a4c8b4', '#84a898'],
  cyan: ['#7fb6bd', '#9fcbd0', '#5f9aa2'],
  yellow: ['#dcaa63', '#efd09a', '#c48f4a'],
  gray: ['#a8acb4', '#b4b8c0', '#989ca4'],
  purple: ['#b0a0c8', '#c0b0d4', '#a090bc'],
  black: ['#9098a4', '#a0a8b0', '#808890']
};

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function hexToRgb(hex) {
  const normalized = hex.replace('#', '');
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16)
  };
}

function rgbToLinear(channel) {
  const value = channel / 255;
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

function linearToRgb(channel) {
  const value = channel <= 0.0031308 ? 12.92 * channel : 1.055 * (channel ** (1 / 2.4)) - 0.055;
  return Math.round(clamp(value * 255, 0, 255));
}

function rgbToOklab(r, g, b) {
  const lr = rgbToLinear(r);
  const lg = rgbToLinear(g);
  const lb = rgbToLinear(b);

  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  return {
    L: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_
  };
}

function oklabToRgb(L, a, b) {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;

  const l = l_ ** 3;
  const m = m_ ** 3;
  const s = s_ ** 3;

  return {
    r: linearToRgb(+4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    g: linearToRgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    b: linearToRgb(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s)
  };
}

function rgbToOklch(r, g, b) {
  const { L, a, b: bb } = rgbToOklab(r, g, b);
  const C = Math.sqrt(a * a + bb * bb);
  let H = Math.atan2(bb, a) * (180 / Math.PI);
  if (H < 0) H += 360;
  return { L, C, H };
}

function oklchToRgb({ L, C, H }) {
  const hRad = (H * Math.PI) / 180;
  return oklabToRgb(L, C * Math.cos(hRad), C * Math.sin(hRad));
}

export function rgbToHsl(r, g, b) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case rn:
        h = ((gn - bn) / delta) % 6;
        break;
      case gn:
        h = (bn - rn) / delta + 2;
        break;
      default:
        h = (rn - gn) / delta + 4;
        break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  return { h, s, l };
}

export function hslToRgb(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rp = 0;
  let gp = 0;
  let bp = 0;

  if (h < 60) {
    rp = c; gp = x; bp = 0;
  } else if (h < 120) {
    rp = x; gp = c; bp = 0;
  } else if (h < 180) {
    rp = 0; gp = c; bp = x;
  } else if (h < 240) {
    rp = 0; gp = x; bp = c;
  } else if (h < 300) {
    rp = x; gp = 0; bp = c;
  } else {
    rp = c; gp = 0; bp = x;
  }

  return {
    r: Math.round((rp + m) * 255),
    g: Math.round((gp + m) * 255),
    b: Math.round((bp + m) * 255)
  };
}

function rgbToCss({ r, g, b }, alpha = 1) {
  if (alpha >= 1) return `rgb(${r}, ${g}, ${b})`;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function hueDistance(h1, h2) {
  const diff = Math.abs(h1 - h2) % 360;
  return diff > 180 ? 360 - diff : diff;
}

function quantizePixel(r, g, b) {
  return {
    r: Math.round(r / 18) * 18,
    g: Math.round(g / 18) * 18,
    b: Math.round(b / 18) * 18
  };
}

function isUnusableOklch({ L, C }) {
  // 纯白 / 近白无信息
  if (L > 0.96 || (C < 0.008 && L > 0.9)) return true;
  // 死黑几乎无色相，只会把主色拉成「黑+脏橙」
  if (L < 0.06) return true;
  // 深阴影且低饱和：面积大但对氛围主色无贡献
  if (L < MOOD_MIN_LIGHTNESS && C < MOOD_SHADOW_CHROMA) return true;
  return false;
}

/** 主色偏好：中明度 + 高饱和；强惩罚阴影与惨白 */
function moodColorWeight({ L, C }) {
  let lightW = 1;
  if (L < 0.18) lightW = 0.12 + L * 1.8;
  else if (L < 0.32) lightW = 0.5 + (L - 0.18) * 2.2;
  else if (L <= 0.72) lightW = 1.2;
  else if (L < 0.88) lightW = 0.9;
  else lightW = 0.32;

  const chromaW = C < 0.02 ? 0.22 : 0.4 + C * 5.2;
  return lightW * chromaW;
}

function isMoodCluster(cluster) {
  return (
    cluster.chroma >= 0.04
    || (cluster.lum >= 0.2 && cluster.lum <= 0.86 && cluster.chroma >= 0.018)
  );
}

/** 优先用「有情绪的色簇」；小面积点缀不应把大面积中性底色整体踢出候选 */
function getMoodClusters(clusters) {
  const mood = clusters.filter(isMoodCluster);
  if (mood.length < 2) return clusters;

  const sorted = [...clusters].sort((a, b) => b.count - a.count);
  const dominant = sorted[0];
  const moodCount = mood.reduce((sum, c) => sum + c.count, 0);
  const total = clusters.reduce((sum, c) => sum + c.count, 0);
  if (total <= 0) return clusters;

  const moodRatio = moodCount / total;
  // 仅当情绪色在画面中占比较高时，才偏向 mood 子集（原 6% 过低，小色块即可触发）
  if (moodRatio < 0.28) return clusters;

  // 即使启用 mood 子集，也保留面积最大的底色簇，避免主色被小点缀抢走
  if (dominant && !mood.includes(dominant)) {
    return [dominant, ...mood];
  }
  return mood;
}

function hashString(input = '') {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = ((hash << 5) - hash) + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function seededUnit(seed, salt) {
  const value = Math.sin(seed * 12.9898 + salt * 78.233) * 43758.5453;
  return value - Math.floor(value);
}

/** 仅修正高饱和荧光黄绿；暗色/棕色/中性色保持原色相 */
function correctExtremeHue(H, C) {
  if (C > 0.11 && H >= 88 && H <= 118) {
    return H > 103 ? H - 6 : H + 6;
  }
  return H;
}

/** 按封面原明度比例提亮：暗封面弱提升保留深色氛围，中亮封面维持原观感 */
function mapLightnessForDisplay(sourceL, role) {
  const liftStrength = sourceL < 0.32 ? 0.2 : sourceL < 0.48 ? 0.4 : 0.52;
  let L = sourceL + (0.6 - sourceL) * liftStrength;

  if (role === 'secondary') L += sourceL < 0.32 ? 0.12 : 0.16;
  else if (role === 'accent') L -= 0.15;

  return clamp(L, DISPLAY_L_MIN, DISPLAY_L_MAX);
}

/** 保留原饱和度比例；中性色不强行注入色相 */
function normalizeChroma(sourceC, sourceL) {
  if (sourceC < 0.022) {
    return clamp(sourceC * 0.88, 0.003, 0.022);
  }

  const maxC = sourceL < 0.34 ? 0.115 : DISPLAY_CHROMA_MAX;
  return clamp(sourceC * 0.96, 0.024, maxC);
}

function optimizeAppleColor(rgb, role) {
  const source = rgbToOklch(rgb.r, rgb.g, rgb.b);
  const H = correctExtremeHue(source.H, source.C);
  const C = normalizeChroma(source.C, source.L);
  const L = mapLightnessForDisplay(source.L, role);
  return oklchToRgb({ L, C, H });
}

function attachPaletteShares(palette, primaryCluster, secondaryCluster, accentCluster, totalCount) {
  const total = totalCount > 0
    ? totalCount
    : (primaryCluster?.count || 0) + (secondaryCluster?.count || 0) + (accentCluster?.count || 0);

  if (total <= 0) {
    return {
      ...palette,
      primaryShare: 0.45,
      secondaryShare: 0.32,
      accentShare: 0.23
    };
  }

  return {
    ...palette,
    primaryShare: (primaryCluster?.count || 0) / total,
    secondaryShare: (secondaryCluster?.count || 0) / total,
    accentShare: (accentCluster?.count || 0) / total
  };
}

/** 弥散色块尺寸/透明度随封面占比缩放，避免小面积点缀色铺满背景 */
function getRoleBlobStyle(share, role) {
  const referenceShare = 0.34;
  const t = clamp(share / referenceShare, 0.26, 1.12);
  const roleScale = role === 'primary' ? 1.05 : role === 'secondary' ? 0.92 : 0.68;
  const k = t * roleScale;

  return {
    ellipseW: Math.round(clamp(50 + k * 42, 46, 96)),
    ellipseH: Math.round(clamp(46 + k * 38, 42, 90)),
    solidEnd: Math.round(clamp(6 + k * 10, 5, 18)),
    fadeEnd: Math.round(clamp(12 + k * 20, 10, 34)),
    peakAlpha: clamp(0.66 + k * 0.26, 0.66, 0.96),
    midAlpha: clamp(0.42 + k * 0.46, 0.42, 0.92)
  };
}

function getAppleSoftBlobPositions(seed) {
  return {
    primary: {
      x: 44 + seededUnit(seed, 10) * 20,
      y: 68 + seededUnit(seed, 15) * 20
    },
    secondary: {
      x: seededUnit(seed, 11) * 22,
      y: seededUnit(seed, 12) * 20
    },
    accent: {
      x: 72 + seededUnit(seed, 13) * 26,
      y: 58 + seededUnit(seed, 14) * 34
    }
  };
}

/** 保证肉眼能分辨 2~3 个色调：拉开明度，必要时轻微错开色相 */
function ensureVisualSeparation(palette) {
  const primary = rgbToOklch(palette.primary.r, palette.primary.g, palette.primary.b);
  let secondary = rgbToOklch(
    palette.secondary.r,
    palette.secondary.g,
    palette.secondary.b
  );
  let accent = rgbToOklch(palette.accent.r, palette.accent.g, palette.accent.b);

  const lightness = [primary.L, secondary.L, accent.L];
  const lSpread = Math.max(...lightness) - Math.min(...lightness);

  if (lSpread < 0.16) {
    secondary = {
      ...secondary,
      L: clamp(primary.L + 0.19, DISPLAY_L_MIN, DISPLAY_L_MAX),
      C: clamp(secondary.C * 1.12, 0.026, DISPLAY_CHROMA_MAX)
    };
    accent = {
      ...accent,
      L: clamp(primary.L - 0.17, DISPLAY_L_MIN, DISPLAY_L_MAX),
      C: clamp(accent.C * 1.08, 0.022, DISPLAY_CHROMA_MAX)
    };
  }

  const hues = [primary.H, secondary.H, accent.H];
  const hueSpread = Math.max(
    hueDistance(hues[0], hues[1]),
    hueDistance(hues[1], hues[2]),
    hueDistance(hues[0], hues[2])
  );

  if (primary.C > 0.02 && hueSpread < 24) {
    secondary = { ...secondary, H: (primary.H + 26) % 360 };
    accent = { ...accent, H: (primary.H + 334) % 360 };
  }

  return {
    ...palette,
    secondary: oklchToRgb(secondary),
    accent: oklchToRgb(accent)
  };
}

function buildDerivedThreeTonePalette(rgb) {
  const source = rgbToOklch(rgb.r, rgb.g, rgb.b);
  const H = correctExtremeHue(source.H, source.C);
  const C = normalizeChroma(source.C, source.L);
  const primaryL = mapLightnessForDisplay(source.L, 'primary');

  if (C < 0.025) {
    return attachPaletteShares(
      ensureVisualSeparation({
        primary: oklchToRgb({ L: primaryL, C, H }),
        secondary: oklchToRgb({
          L: clamp(primaryL + 0.12, DISPLAY_L_MIN, DISPLAY_L_MAX),
          C,
          H
        }),
        accent: oklchToRgb({
          L: clamp(primaryL - 0.1, DISPLAY_L_MIN, DISPLAY_L_MAX),
          C,
          H
        }),
        isMonochrome: false
      }),
      { count: 1 },
      null,
      null,
      1
    );
  }

  return attachPaletteShares(
    ensureVisualSeparation({
      primary: oklchToRgb({ L: primaryL, C, H }),
      secondary: oklchToRgb({
        L: clamp(primaryL + 0.11, DISPLAY_L_MIN, DISPLAY_L_MAX),
        C: clamp(C * 1.08, 0.02, DISPLAY_CHROMA_MAX),
        H: (H + 12) % 360
      }),
      accent: oklchToRgb({
        L: clamp(primaryL - 0.09, DISPLAY_L_MIN, DISPLAY_L_MAX),
        C: clamp(C * 0.94, 0.018, DISPLAY_CHROMA_MAX),
        H: (H + 348) % 360
      }),
      isMonochrome: false
    }),
    { count: 0.58 },
    { count: 0.28 },
    { count: 0.14 },
    1
  );
}

function pickPrimary(clusters, totalCount) {
  let best = clusters[0];
  let bestScore = -1;
  const total = totalCount > 0 ? totalCount : clusters.reduce((sum, c) => sum + c.count, 0);
  const sortedByCount = [...clusters].sort((a, b) => b.count - a.count);
  const dominantCluster = sortedByCount[0] || null;
  const dominantShare = dominantCluster && total > 0 ? dominantCluster.count / total : 0;

  for (const cluster of clusters) {
    const share = total > 0 ? cluster.count / total : 0;
    // 小面积纯色/高饱和点缀：占比过低时强降权，避免弥散背景偏离封面整体氛围
    const minPrimaryShare = 0.1;
    const shareFactor = share >= minPrimaryShare
      ? 1
      : (share / minPrimaryShare) ** 1.75;

    const area = cluster.count ** 0.88;
    const score = area
      * moodColorWeight(cluster.oklch)
      * (0.55 + share * 2.6)
      * (1 + Math.min(cluster.chroma, 0.11) * 0.65)
      * shareFactor;

    if (score > bestScore) {
      bestScore = score;
      best = cluster;
    }
  }

  const bestShare = total > 0 ? best.count / total : 0;
  // 若评分赢家仍是小面积点缀，且存在明显大面积主色，回退到面积最大簇
  if (
    dominantCluster
    && best !== dominantCluster
    && bestShare < 0.14
    && dominantShare >= 0.2
  ) {
    return dominantCluster;
  }

  return best;
}

function pickSecondary(clusters, primary, totalCount) {
  let best = null;
  let bestScore = -1;
  const total = totalCount > 0 ? totalCount : clusters.reduce((sum, c) => sum + c.count, 0);

  for (const cluster of clusters) {
    if (cluster === primary) continue;

    const share = total > 0 ? cluster.count / total : 0;
    const minSecondaryShare = 0.045;
    const shareFactor = share >= minSecondaryShare
      ? 1
      : (share / minSecondaryShare) ** 1.35;
    const hueDiff = hueDistance(cluster.oklch.H, primary.oklch.H);
    // 高饱和辅色放宽色相窗口（红+黄应能并存），低饱和仍偏同色系
    const maxHue = cluster.chroma >= 0.055 || primary.chroma >= 0.055 ? 100 : 62;
    if (hueDiff > maxHue) continue;

    const lightnessDiff = Math.abs(cluster.lum - primary.lum);
    const hueBonus = Math.min(hueDiff, 55) / 55 * 0.65;
    const score = (cluster.count ** 0.74)
      * moodColorWeight(cluster.oklch)
      * (0.5 + share * 1.8)
      * (0.45 + lightnessDiff * 1.2 + cluster.chroma * 2.2 + hueBonus)
      * shareFactor;

    if (score > bestScore) {
      bestScore = score;
      best = cluster;
    }
  }

  return best;
}

function pickAccent(clusters, primary, secondary, totalCount) {
  let best = null;
  let bestScore = -1;
  const total = totalCount > 0 ? totalCount : clusters.reduce((sum, c) => sum + c.count, 0);

  for (const cluster of clusters) {
    if (cluster === primary || cluster === secondary) continue;

    const share = total > 0 ? cluster.count / total : 0;
    const hueDiffPrimary = hueDistance(cluster.oklch.H, primary.oklch.H);
    const hueDiffSecondary = secondary
      ? hueDistance(cluster.oklch.H, secondary.oklch.H)
      : 0;
    const maxHue = cluster.chroma >= 0.055 ? 110 : 68;
    if (hueDiffPrimary > maxHue) continue;

    const lightnessDiffPrimary = Math.abs(cluster.lum - primary.lum);
    const lightnessDiffSecondary = secondary
      ? Math.abs(cluster.lum - secondary.lum)
      : lightnessDiffPrimary;

    const separationBonus = Math.min(hueDiffPrimary, 70) / 70 * 0.55
      + Math.min(hueDiffSecondary, 50) / 50 * 0.35;

    const score = (cluster.count ** 0.72)
      * moodColorWeight(cluster.oklch)
      * (0.42 + share * 1.5)
      * (
        0.38
        + lightnessDiffPrimary * 1.2
        + lightnessDiffSecondary * 0.75
        + cluster.chroma * 2.4
        + separationBonus
      );

    if (score > bestScore) {
      bestScore = score;
      best = cluster;
    }
  }

  return best;
}

function buildThreeTonePalette(clusters, totalCount = 0) {
  const total = totalCount > 0
    ? totalCount
    : clusters.reduce((sum, cluster) => sum + cluster.count, 0);
  const moodClusters = getMoodClusters(clusters);
  const sorted = [...moodClusters].sort((a, b) => b.count - a.count);
  const primaryCluster = pickPrimary(moodClusters, total);
  let secondaryCluster = pickSecondary(moodClusters, primaryCluster, total);
  let accentCluster = secondaryCluster
    ? pickAccent(moodClusters, primaryCluster, secondaryCluster, total)
    : null;

  if (!secondaryCluster && sorted[1] && sorted[1] !== primaryCluster) {
    secondaryCluster = sorted[1];
  }

  if (!accentCluster) {
    accentCluster = sorted.find(
      (cluster) => cluster !== primaryCluster && cluster !== secondaryCluster
    ) || null;
  }

  // 全图回退：情绪簇不够时从全部簇补辅色
  if ((!secondaryCluster || !accentCluster) && moodClusters !== clusters) {
    if (!secondaryCluster) {
      secondaryCluster = pickSecondary(clusters, primaryCluster, total)
        || clusters.find((c) => c !== primaryCluster)
        || null;
    }
    if (!accentCluster && secondaryCluster) {
      accentCluster = pickAccent(clusters, primaryCluster, secondaryCluster, total)
        || clusters.find((c) => c !== primaryCluster && c !== secondaryCluster)
        || null;
    }
  }

  if (!secondaryCluster || !accentCluster) {
    return attachPaletteShares(
      buildDerivedThreeTonePalette(primaryCluster.rgb),
      primaryCluster,
      null,
      null,
      total
    );
  }

  return attachPaletteShares(
    ensureVisualSeparation({
      primary: optimizeAppleColor(primaryCluster.rgb, 'primary'),
      secondary: optimizeAppleColor(secondaryCluster.rgb, 'secondary'),
      accent: optimizeAppleColor(accentCluster.rgb, 'accent'),
      isMonochrome: false
    }),
    primaryCluster,
    secondaryCluster,
    accentCluster,
    total
  );
}

/**
 * 从图片提取 3 色：Primary / Secondary / Accent
 * @param {ImageData} imageData
 * @param {number} seed
 * @param {number} [width] 采样宽（用于中心加权）
 * @param {number} [height] 采样高
 */
export function extractMoodColors(imageData, seed = 0, width = 0, height = 0) {
  const pixels = imageData.data;
  const colorMap = new Map();
  const pixelCount = pixels.length / 4;
  const w = width > 0 ? width : Math.max(1, Math.round(Math.sqrt(pixelCount)));
  const h = height > 0 ? height : Math.max(1, Math.round(pixelCount / w));

  for (let i = 0; i < pixels.length; i += 4) {
    const alpha = pixels[i + 3];
    if (alpha < 120) continue;

    const quantized = quantizePixel(pixels[i], pixels[i + 1], pixels[i + 2]);
    const oklch = rgbToOklch(quantized.r, quantized.g, quantized.b);
    if (isUnusableOklch(oklch)) continue;

    // 封面主体多在中心：边缘阴影/边框降权，中心红卫衣等焦点色加权
    const px = (i / 4) % w;
    const py = Math.floor((i / 4) / w);
    const nx = (px + 0.5) / w - 0.5;
    const ny = (py + 0.5) / h - 0.5;
    const radial = Math.min(1, Math.sqrt(nx * nx + ny * ny) * 2.05);
    const sampleWeight = 1.4 - radial * 0.65;

    const key = `${quantized.r},${quantized.g},${quantized.b}`;
    if (!colorMap.has(key)) {
      colorMap.set(key, {
        rgb: quantized,
        count: 0,
        totalL: 0,
        totalC: 0
      });
    }

    const entry = colorMap.get(key);
    entry.count += sampleWeight;
    entry.totalL += oklch.L * sampleWeight;
    entry.totalC += oklch.C * sampleWeight;
  }

  const clusters = [...colorMap.values()]
    .map((entry) => {
      const lum = entry.totalL / entry.count;
      const chroma = entry.totalC / entry.count;
      const oklch = rgbToOklch(entry.rgb.r, entry.rgb.g, entry.rgb.b);
      return {
        rgb: entry.rgb,
        count: entry.count,
        lum,
        chroma,
        oklch,
        hsl: rgbToHsl(entry.rgb.r, entry.rgb.g, entry.rgb.b),
        score: entry.count * moodColorWeight(oklch)
      };
    })
    .sort((a, b) => b.score - a.score);

  const totalCount = clusters.reduce((sum, cluster) => sum + cluster.count, 0);
  let palette;

  if (clusters.length === 0) {
    palette = getThemeFallbackColors('pink');
  } else {
    palette = buildThreeTonePalette(clusters, totalCount);
  }

  return {
    ...palette,
    seed
  };
}

function buildAppleGradientBackground(colors) {
  const primary = colors.primary;
  const secondary = colors.secondary || primary;
  const accent = colors.accent || secondary;
  const positions = getAppleSoftBlobPositions(colors.seed || 0);

  const primaryShare = colors.primaryShare ?? 0.45;
  const secondaryShare = colors.secondaryShare ?? 0.32;
  const accentShare = colors.accentShare ?? 0.23;
  const shareSum = primaryShare + secondaryShare + accentShare || 1;

  const primaryBlob = getRoleBlobStyle(primaryShare / shareSum, 'primary');
  const secondaryBlob = getRoleBlobStyle(secondaryShare / shareSum, 'secondary');
  const accentBlob = getRoleBlobStyle(accentShare / shareSum, 'accent');

  const accentStop = Math.round((accentShare / shareSum) * 22 + 6);
  const primaryStop = Math.round(accentStop + (primaryShare / shareSum) * 38 + 10);

  // 底层→顶层：主色铺底，辅色中等，点缀色最小且在最上层
  return [
    `radial-gradient(ellipse ${primaryBlob.ellipseW}% ${primaryBlob.ellipseH}% at ${positions.primary.x}% ${positions.primary.y}%, ${rgbToCss(primary)} 0%, ${rgbToCss(primary, primaryBlob.midAlpha)} ${primaryBlob.solidEnd}%, transparent ${primaryBlob.fadeEnd}%)`,
    `radial-gradient(ellipse ${secondaryBlob.ellipseW}% ${secondaryBlob.ellipseH}% at ${positions.secondary.x}% ${positions.secondary.y}%, ${rgbToCss(secondary)} 0%, ${rgbToCss(secondary, secondaryBlob.midAlpha)} ${secondaryBlob.solidEnd}%, transparent ${secondaryBlob.fadeEnd}%)`,
    `radial-gradient(ellipse ${accentBlob.ellipseW}% ${accentBlob.ellipseH}% at ${positions.accent.x}% ${positions.accent.y}%, ${rgbToCss(accent)} 0%, ${rgbToCss(accent, accentBlob.midAlpha)} ${accentBlob.solidEnd}%, transparent ${accentBlob.fadeEnd}%)`,
    `linear-gradient(155deg, ${rgbToCss(accent, 0.72)} 0%, ${rgbToCss(primary)} ${primaryStop}%, ${rgbToCss(secondary)} 100%)`
  ].join(', ');
}

/**
 * Apple Music 风格：主色大面积底色 + 两辅助色柔和色块，经超大 blur 融合
 */
export function buildMeshGradientBackground(colors) {
  return buildAppleGradientBackground(colors);
}

export function getThemeFallbackColors(themeMode = 'pink') {
  const hexes = THEME_FALLBACK_HEX[themeMode] || THEME_FALLBACK_HEX.pink;

  return {
    ...attachPaletteShares(
      ensureVisualSeparation({
        primary: optimizeAppleColor(hexToRgb(hexes[0]), 'primary'),
        secondary: optimizeAppleColor(hexToRgb(hexes[1]), 'secondary'),
        accent: optimizeAppleColor(hexToRgb(hexes[2]), 'accent'),
        isMonochrome: false
      }),
      { count: 0.42 },
      { count: 0.33 },
      { count: 0.25 },
      1
    ),
    seed: hashString(themeMode)
  };
}

function getCacheKey(url) {
  return `${CACHE_VERSION}:${url}`;
}

function trimCache() {
  if (COLOR_CACHE.size <= CACHE_MAX_SIZE) return;
  COLOR_CACHE.delete(COLOR_CACHE.keys().next().value);
}

export function loadImageElement(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    image.src = url;
  });
}

export function extractColorsFromImage(image, sampleSize = 96, seed = 0) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    return getThemeFallbackColors();
  }

  const ratio = image.width / image.height || 1;
  let width = sampleSize;
  let height = sampleSize;

  if (ratio >= 1) {
    height = Math.max(24, Math.round(sampleSize / ratio));
  } else {
    width = Math.max(24, Math.round(sampleSize * ratio));
  }

  canvas.width = width;
  canvas.height = height;
  context.drawImage(image, 0, 0, width, height);
  const imageData = context.getImageData(0, 0, width, height);
  return extractMoodColors(imageData, seed, width, height);
}

export async function extractColorsFromImageUrl(url, options = {}) {
  const { themeMode = 'pink', forceRefresh = false } = options;
  if (!url) {
    return getThemeFallbackColors(themeMode);
  }

  const cacheKey = getCacheKey(url);
  if (!forceRefresh && COLOR_CACHE.has(cacheKey)) {
    return COLOR_CACHE.get(cacheKey);
  }

  try {
    const image = await loadImageElement(url);
    const colors = extractColorsFromImage(image, 96, hashString(url));
    COLOR_CACHE.set(cacheKey, colors);
    trimCache();
    return colors;
  } catch (error) {
    const fallback = getThemeFallbackColors(themeMode);
    COLOR_CACHE.set(cacheKey, fallback);
    trimCache();
    return fallback;
  }
}

export function isNeutralColorPalette(colors) {
  const samples = [colors?.primary, colors?.secondary, colors?.accent].filter(Boolean);
  if (samples.length === 0) return false;
  const avgChroma = samples.reduce(
    (sum, rgb) => sum + rgbToOklch(rgb.r, rgb.g, rgb.b).C,
    0
  ) / samples.length;
  return avgChroma < NEUTRAL_IMAGE_CHROMA;
}

export function buildMeshBackgroundStyle(colors) {
  const palette = colors?.primary
    ? colors
    : getThemeFallbackColors();

  return {
    backgroundColor: rgbToCss(palette.primary),
    backgroundImage: buildMeshGradientBackground(palette)
  };
}

export const DEFAULT_MESH_COLORS = getThemeFallbackColors('pink');
